import { useForm } from 'react-hook-form'
import { gql } from 'graphql-request'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogClose
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useMutation } from '@tanstack/react-query'
import { request } from '@helpers/graphql'
import { useFlashMessage } from '@components/FlashMessage'
import { Loader2 } from 'lucide-react'

const EXTEND_EDUCATOR_TRIALS = gql`
  mutation AdminExtendEducatorTrials($adminExtendEducatorTrialsInput: AdminExtendEducatorTrialsInput!) {
    adminExtendEducatorTrials(input: $adminExtendEducatorTrialsInput) {
      success
      errors
    }
  }
`

const ExtendTrialsModal = ({ isOpen, closeModal, organizationId }) => {
    const { setFlashMessage } = useFlashMessage()
    const { register, handleSubmit, setError, formState: { errors } } = useForm({
        defaultValues: {
            daysToExtend: 30
        }
    })

    const { mutate: extendTrials, isLoading } = useMutation({
        mutationFn: (variables) => request(EXTEND_EDUCATOR_TRIALS, variables),
        onSuccess: (response) => {
            if (response?.adminExtendEducatorTrials?.success) {
                setFlashMessage('Trials successfully extended', 'success')
                closeModal()
            } else {
                const errorMessage = response?.adminExtendEducatorTrials?.errors?.[0] || 'Failed to extend trials'
                setError('general', { message: errorMessage })
            }
        },
        onError: (error) => {
            console.error('Error extending trials:', error)
            setError('general', { message: error.message || 'An unexpected error occurred' })
        }
    })

    const onSubmit = (data) => {
        extendTrials({
            adminExtendEducatorTrialsInput: {
                organizationId,
                daysToExtend: parseInt(data.daysToExtend)
            }
        })
    }

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Extend Educator Trials</DialogTitle>
                    <DialogDescription className="text-gray-600 dark:text-gray-400">
                        Please note this is going to extend all the educator trials and sync them to the same schedule.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div>
                        <Label htmlFor="daysToExtend">Number of days to extend</Label>
                        <Input
                            id="daysToExtend"
                            type="number"
                            min={1}
                            max={365}
                            {...register('daysToExtend', {
                                required: 'Number of days is required',
                                min: { value: 1, message: 'Minimum 1 day' },
                                max: { value: 365, message: 'Maximum 365 days' }
                            })}
                            className="w-full mt-1"
                        />
                        {errors.daysToExtend && (
                            <p className="mt-1 text-sm text-destructive" role="alert">
                                {errors.daysToExtend.message}
                            </p>
                        )}
                    </div>

                    {errors.general && (
                        <p className="text-sm text-destructive" role="alert">
                            {errors.general.message}
                        </p>
                    )}

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" variant="outline">
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button
                            type='submit'
                            disabled={isLoading}
                            loading={isLoading}
                        >
                            {isLoading ? 'Extending...' : 'Extend trials'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default ExtendTrialsModal
